import { Box } from '@mui/material';
import { BoxProps } from '@mui/material/Box/Box';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { OrganizationUnit } from '~/data/orgunits/types';
import { WorkflowConfig } from '~/data/workflows';
import { WorkflowConfigDialog } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigDialog';
import { WorkflowRowActions } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigurationsTable/component/WorkflowRowActions';
import { WorkflowSummary } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigurationsTable/component/WorkflowSummary';
import { useWorkflowConfigsData } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigurationsTable/useWorkflowConfigsData';
import { useWorkflowConfigurationsGridData } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/components/WorkflowConfigurationsTable/useWorkflowConfigurationsGridData';
import { theme } from '~/theme';

import { useSxStyles } from './useStyles';

export type WorkflowEditProps = {
  isEdit: boolean;
  workflowConfig: WorkflowConfig;
};

type PropsType = BoxProps & {
  orgUnit: OrganizationUnit | undefined;
  orgUnits: OrganizationUnit[];
  unitConfigsEnabled: boolean;
};

export const WorkflowConfigurationsTable = (props: PropsType) => {
  const { t } = useTranslation();
  const { orgUnit, orgUnits, unitConfigsEnabled, ...boxProps } = props;
  const activeUnitId = useMemo(() => orgUnit?.fOrgUnitId || 0, [orgUnit]);
  const [editConfig, setEditConfig] = useState<WorkflowEditProps | undefined>(
    undefined,
  );

  const { unitWorkflowConfigs, deleteWorkflow } = useWorkflowConfigsData({
    unitId: activeUnitId,
  });
  const { gridData } = useWorkflowConfigurationsGridData({
    unitWorkflowConfigs: unitWorkflowConfigs,
  });

  const handleEditWorkflow = useCallback((editProps: WorkflowEditProps) => {
    setEditConfig(editProps);
  }, []);

  const handleCloseEditDialog = () => {
    setEditConfig(undefined);
  };

  const columnsConfig: GridColDef[] = [
    {
      field: 'type',
      headerName: t('laWorkFlow'),
      minWidth: 200,
    },
    {
      field: 'initiators',
      headerName: t('workflowInitiator'),
      minWidth: 150,
    },
    {
      field: 'stepsSummary',
      headerName: t('approvalProcess'),
      minWidth: 250,
    },
    {
      field: 'closer',
      headerName: t('workflowCloser'),
      minWidth: 150,
    },
    {
      field: 'overseer',
      headerName: t('workflowOverseer'),
      minWidth: 150,
    },
    {
      field: 'administrator',
      headerName: t('workflowAdministrator'),
      minWidth: 150,
    },
    {
      field: 'meta',
      headerName: t('actions'),
      minWidth: 130,
      renderCell: ({ row }) => {
        return (
          <WorkflowRowActions
            orgUnit={orgUnit}
            workflowConfig={row.meta.configData}
            workflowState={row.meta.configState}
            onEdit={handleEditWorkflow}
            onDelete={deleteWorkflow}
            unitConfigsEnabled={unitConfigsEnabled}
          />
        );
      },
    },
  ];

  const pinnedColumns = {
    left: ['__detail_panel_toggle__', 'type'],
  };

  const columnGroupingConfig: GridColumnGroupingModel = [
    {
      groupId: t('workflowProcess'),
      children: [
        { field: 'initiators' },
        { field: 'stepsSummary' },
        { field: 'closer' },
      ],
    },
    {
      groupId: t('workflowSupervisoryRoles'),
      children: [{ field: 'overseer' }, { field: 'administrator' }],
    },
  ];

  const getTableHeight = (rowCount: number) => {
    const gridHeader = 112;
    const gridFooter = 52;
    const maxRowCount = 10;

    return `${
      (rowCount <= maxRowCount ? rowCount : maxRowCount) * 52 +
      gridHeader +
      gridFooter +
      2
    }px`;
  };

  const sxStyles = useSxStyles();
  const { sx, ...restBoxProps } = boxProps;

  return (
    <Box sx={{ ...sxStyles.gridView, ...sx }} {...restBoxProps}>
      <Box
        sx={{
          ...sxStyles.gridContainer,
          minHeight: getTableHeight(gridData.length),
        }}
      >
        <SolaForceDataGrid
          columns={columnsConfig}
          columnGroupingModel={columnGroupingConfig}
          rows={gridData}
          getDetailPanelContent={({ row }) => (
            <WorkflowSummary
              workflowConfig={row.meta.configData}
              workflowState={row.meta.configState}
              sx={{ borderBottom: `solid 1px ${theme.palette.divider}` }}
              onEdit={handleEditWorkflow}
              orgUnits={orgUnits}
            />
          )}
          getDetailPanelHeight={() => 'auto'}
          getRowClassName={(params) =>
            `workflow-row--${params.row.meta.configState}`
          }
          sx={sxStyles.dataGrid}
          disableRowSelectionOnClick={true}
          pinnedColumns={pinnedColumns}
        />
        {editConfig && (
          <WorkflowConfigDialog
            activeUnitId={activeUnitId}
            orgUnit={orgUnit}
            open={!!editConfig}
            onClose={handleCloseEditDialog}
            workflowConfig={editConfig.workflowConfig}
            editMode={editConfig.isEdit}
            unitConfigsEnabled={unitConfigsEnabled}
          />
        )}
      </Box>
    </Box>
  );
};
